import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { gaEvent, CONVERSION_EVENTS } from "../lib/analytics";

/**
 * OPIS Founding Member application modal.
 *
 * Submits directly to the live OPIS portal application endpoint. The marketing
 * site does not persist these applications; the OPIS portal is the source of
 * truth. Payload schema is fixed by the OPIS portal API.
 */
const OPIS_APPLY_ENDPOINT = "https://opis.optimumhospitalitysolutions.com/api/apply";

export const OpisAccessModal = ({ open, onOpenChange }) => {
    const [form, setForm] = useState({
        name: "",
        company: "",
        email: "",
        country: "",
        phone: "",
        role: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const reset = () => {
        setForm({ name: "", company: "", email: "", country: "", phone: "", role: "", message: "" });
        setSubmitted(false);
    };

    const handleClose = (next) => {
        onOpenChange(next);
        if (!next) setTimeout(reset, 300);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.company || !form.email || !form.country) {
            toast.error("Please complete your name, company, email and country.");
            return;
        }
        setSubmitting(true);
        try {
            // Fold telephone into the message body since the OPIS API schema
            // does not include a dedicated phone field.
            const messageParts = [];
            if (form.message) messageParts.push(form.message.trim());
            if (form.phone) messageParts.push(`Telephone: ${form.phone.trim()}`);
            const message = messageParts.join("\n\n");

            const payload = {
                name: form.name.trim(),
                email: form.email.trim(),
                company: form.company.trim(),
                role: form.role || "",
                country: form.country.trim(),
                message,
                source: "marketing_website",
            };

            const res = await fetch(OPIS_APPLY_ENDPOINT, {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                throw new Error(`OPIS apply failed with status ${res.status}`);
            }

            gaEvent(CONVERSION_EVENTS.CONTACT_SUBMIT, {
                source: "opis_founding_member_modal",
            });

            setSubmitted(true);
        } catch {
            toast.error(
                "We couldn\u2019t submit your application. Please try again, or contact us at info@optimumhospitalitysolutions.com."
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent
                data-testid="opis-access-modal"
                className="max-w-lg bg-brand-stone border border-brand-border rounded-none p-8 sm:p-10"
            >
                {submitted ? (
                    <div data-testid="opis-access-modal-success" className="text-center py-4">
                        <div className="mx-auto w-12 h-12 bg-brand-champagne/15 flex items-center justify-center mb-6">
                            <Check className="text-brand-champagne" size={22} strokeWidth={1.5} />
                        </div>
                        <h3 className="font-display text-2xl text-brand-navy leading-snug">
                            Application received.
                        </h3>
                        <p className="mt-4 text-brand-ink/75 leading-relaxed">
                            Thank you for your interest in becoming an OPIS<sup className="text-[9px]">™</sup> Founding Member. Your application will be personally reviewed by our team, and we&rsquo;ll be in touch shortly.
                        </p>
                        <Button
                            data-testid="opis-access-modal-close"
                            onClick={() => handleClose(false)}
                            className="mt-6 bg-brand-navy text-brand-stone hover:bg-brand-hover rounded-none px-6"
                        >
                            Close
                        </Button>
                    </div>
                ) : (
                    <>
                        <DialogHeader>
                            <span className="text-[10px] uppercase tracking-[0.25em] text-brand-champagne font-medium">
                                OPIS™ Founding Member Programme
                            </span>
                            <DialogTitle className="font-display text-2xl text-brand-navy leading-snug mt-3">
                                Apply for Founding Member Access
                            </DialogTitle>
                            <DialogDescription className="text-brand-ink/75 leading-relaxed mt-3 text-sm">
                                Founding Member applications are now open. Each application is reviewed personally by Sidney Adams. Please share a few details and we will be in touch.
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={submit} className="space-y-4 mt-6" data-testid="opis-access-form">
                            <div>
                                <Label htmlFor="opis-name" className="text-xs uppercase tracking-[0.18em] text-brand-ink/60">Name</Label>
                                <Input
                                    id="opis-name"
                                    data-testid="opis-access-name"
                                    value={form.name}
                                    onChange={(e) => set("name", e.target.value)}
                                    className="mt-2 rounded-none border-brand-border bg-white"
                                    autoComplete="name"
                                    required
                                />
                            </div>
                            <div>
                                <Label htmlFor="opis-company" className="text-xs uppercase tracking-[0.18em] text-brand-ink/60">Hotel or company</Label>
                                <Input
                                    id="opis-company"
                                    data-testid="opis-access-company"
                                    value={form.company}
                                    onChange={(e) => set("company", e.target.value)}
                                    className="mt-2 rounded-none border-brand-border bg-white"
                                    autoComplete="organization"
                                    required
                                />
                            </div>
                            <div>
                                <Label htmlFor="opis-email" className="text-xs uppercase tracking-[0.18em] text-brand-ink/60">Email</Label>
                                <Input
                                    id="opis-email"
                                    data-testid="opis-access-email"
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => set("email", e.target.value)}
                                    className="mt-2 rounded-none border-brand-border bg-white"
                                    autoComplete="email"
                                    required
                                />
                            </div>
                            <div>
                                <Label htmlFor="opis-country" className="text-xs uppercase tracking-[0.18em] text-brand-ink/60">Country</Label>
                                <Input
                                    id="opis-country"
                                    data-testid="opis-access-country"
                                    value={form.country}
                                    onChange={(e) => set("country", e.target.value)}
                                    className="mt-2 rounded-none border-brand-border bg-white"
                                    autoComplete="country-name"
                                    placeholder="United Kingdom"
                                    required
                                />
                            </div>
                            <div>
                                <Label htmlFor="opis-phone" className="text-xs uppercase tracking-[0.18em] text-brand-ink/60">
                                    Telephone <span className="text-brand-ink/40 normal-case tracking-normal">(optional)</span>
                                </Label>
                                <Input
                                    id="opis-phone"
                                    data-testid="opis-access-phone"
                                    type="tel"
                                    value={form.phone}
                                    onChange={(e) => set("phone", e.target.value)}
                                    className="mt-2 rounded-none border-brand-border bg-white"
                                    autoComplete="tel"
                                />
                            </div>
                            <div>
                                <Label htmlFor="opis-role" className="text-xs uppercase tracking-[0.18em] text-brand-ink/60">
                                    Current role <span className="text-brand-ink/40 normal-case tracking-normal">(optional)</span>
                                </Label>
                                <select
                                    id="opis-role"
                                    data-testid="opis-access-role"
                                    value={form.role}
                                    onChange={(e) => set("role", e.target.value)}
                                    className="mt-2 w-full h-10 px-3 rounded-none border border-brand-border bg-white text-sm text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand-champagne/40"
                                >
                                    <option value="">Select if you wish</option>
                                    <option value="Owner">Owner</option>
                                    <option value="General Manager">General Manager</option>
                                    <option value="Operations Director">Operations Director</option>
                                    <option value="Regional Director">Regional Director</option>
                                    <option value="Asset Manager">Asset Manager</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                            <div>
                                <Label htmlFor="opis-message" className="text-xs uppercase tracking-[0.18em] text-brand-ink/60">
                                    Anything you would like us to know <span className="text-brand-ink/40 normal-case tracking-normal">(optional)</span>
                                </Label>
                                <textarea
                                    id="opis-message"
                                    data-testid="opis-access-message"
                                    value={form.message}
                                    onChange={(e) => set("message", e.target.value)}
                                    rows={3}
                                    className="mt-2 w-full px-3 py-2 rounded-none border border-brand-border bg-white text-sm text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand-champagne/40"
                                />
                            </div>
                            <Button
                                type="submit"
                                data-testid="opis-access-submit"
                                disabled={submitting}
                                className="w-full bg-brand-navy text-brand-stone hover:bg-brand-hover rounded-none py-6 tracking-wide"
                            >
                                {submitting ? (
                                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending</>
                                ) : (
                                    "Submit application"
                                )}
                            </Button>
                            <p className="text-[11px] text-brand-ink/55 text-center leading-relaxed">
                                By submitting this application you agree to be contacted by Optimum Hospitality Solutions Ltd about the OPIS™ Founding Member Programme. Your details are held under our Privacy Policy.
                            </p>
                        </form>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};
